import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import ExcelJS from 'exceljs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import PageHeader from '../../components/shared/PageHeader';
import { APIHeader } from '../../index';
import {
  createGetPECRAsActorOptions,
  createGetPECRAsCreatorOptions,
  createGetPECRAsOwnerOptions,
} from '../../queries/listQueries';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import {
  ListFilterTableGrid,
  homeButtonComponent,
  modernContentSection,
} from '../../styles';
import { getDownloadLink, isEmptyObject } from '../../utils';
import Filter from '../ListTableAndTrackingFIle/Filter';
import ListTable from '../ListTableAndTrackingFIle/ListTable';

//My P/ECR Page Component
function MyPecr(props) {
  const { apiConfig, userInfo, productFamilyList, errorInAPI } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const urlFilterRef = useRef(false);

  const myPecrType = (type || 'all').toLowerCase();
  const isRoleBasedMyPecr = ['owner', 'creator', 'actor'].includes(myPecrType);
  const isAllMyPecr = !isRoleBasedMyPecr;

  const pageTitle = useMemo(() => {
    if (myPecrType === 'creator') {
      return `${t('My_PECR_List')} (${t('Creator')})`;
    }

    if (myPecrType === 'owner') {
      return `${t('My_PECR_List')} (${t('Owner')})`;
    }

    if (myPecrType === 'actor') {
      return `${t('My_PECR_List')} (${t('Actor')})`;
    }

    return t('My_PECR_List');
  }, [myPecrType, t]);

  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [siteListData, setSiteListData] = useState([]);
  const [statusListData, setStatusListData] = useState([]);
  const [filterProdFamData, setFilterProdFamData] = useState([]);
  const [filterSiteData, setFilterSiteData] = useState([]);
  const [filterStatusData, setFilterStatusData] = useState([]);
  const [filterRoleData, setFilterRoleData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    selectedStatus: '',
    selectedSites: '',
    selectedProductFamily: '',
    selectedRole: '',
  });
  const [columnOrderData, setColumnOrderData] = useState({
    columnId: '',
    order: '',
  });

  const myPecrPayload = useMemo(() => {
    if (!userInfo?.userID) {
      return null;
    }

    return {
      userId: userInfo.userID.toString(),
    };
  }, [userInfo]);

  const { data: ownerPecrData = [], isFetching: isFetchingOwnerPecr } = useQuery(
    createGetPECRAsOwnerOptions(myPecrPayload || {}, {
      enabled: !!myPecrPayload,
    })
  );

  const { data: creatorPecrData = [], isFetching: isFetchingCreatorPecr } =
    useQuery(
      createGetPECRAsCreatorOptions(myPecrPayload || {}, {
        enabled: !!myPecrPayload,
      })
    );

  const { data: actorPecrData = [], isFetching: isFetchingActorPecr } = useQuery(
    createGetPECRAsActorOptions(myPecrPayload || {}, {
      enabled: !!myPecrPayload,
    })
  );

  const rolePecrIds = useMemo(() => {
    if (myPecrType === 'owner') {
      return new Set((ownerPecrData || []).map(item => String(item.PECRID)));
    }

    if (myPecrType === 'creator') {
      return new Set((creatorPecrData || []).map(item => String(item.PECRID)));
    }

    if (myPecrType === 'actor') {
      return new Set((actorPecrData || []).map(item => String(item.PECRID)));
    }

    return null;
  }, [myPecrType, ownerPecrData, creatorPecrData, actorPecrData]);

  const allRolePecrIds = useMemo(() => {
    const selectedRole = selectedFilters.selectedRole
      ? selectedFilters.selectedRole.split(',')
      : [];

    if (!isAllMyPecr || selectedRole.length === 0) {
      return null;
    }

    const selectedRoleSet = new Set(selectedRole.map(item => item.toLowerCase()));
    const allIds = new Set();

    if (selectedRoleSet.has('owner')) {
      (ownerPecrData || []).forEach(item => allIds.add(String(item.PECRID)));
    }

    if (selectedRoleSet.has('creator')) {
      (creatorPecrData || []).forEach(item => allIds.add(String(item.PECRID)));
    }

    if (selectedRoleSet.has('actor')) {
      (actorPecrData || []).forEach(item => allIds.add(String(item.PECRID)));
    }

    return allIds;
  }, [
    selectedFilters.selectedRole,
    isAllMyPecr,
    ownerPecrData,
    creatorPecrData,
    actorPecrData,
  ]);

  const displayListData = useMemo(() => {
    if (isAllMyPecr) {
      if (!allRolePecrIds) {
        return listData;
      }

      return listData.filter(item => allRolePecrIds.has(String(item.ID)));
    }

    if (!rolePecrIds) {
      return listData;
    }

    return listData.filter(item => rolePecrIds.has(String(item.ID)));
  }, [isAllMyPecr, allRolePecrIds, rolePecrIds, listData]);

  const isRoleListLoading =
    isRoleBasedMyPecr &&
    ((myPecrType === 'owner' && isFetchingOwnerPecr) ||
      (myPecrType === 'creator' && isFetchingCreatorPecr) ||
      (myPecrType === 'actor' && isFetchingActorPecr));

  const handleDownloadExcel = async () => {
    if (!displayListData || displayListData.length === 0) {
      return;
    }

    const exportColumns = [
      { key: 'ID', label: t('ID') },
      { key: 'Status', label: t('Status') },
      { key: 'Title', label: t('Title') },
      { key: 'CreatedDateSort', label: t('Creation_Date') },
      { key: 'UserName', label: t('Site_Creator') },
      { key: 'Description', label: t('Description') },
      { key: 'ProductFamilyID', label: t('Product_Family') },
      { key: 'SitesImpacted', label: t('Sites_Impacted') },
    ];

    const formatDate = value => {
      if (!value) {
        return '';
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString();
    };

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('My PECR List');

    worksheet.columns = exportColumns.map(column => ({
      header: column.label,
      key: column.key,
      width: 15,
    }));

    const normalizedRows = displayListData.map(row => ({
      ID: row.ID,
      Status: row.Status,
      Title: row.Title,
      CreatedDateSort: formatDate(row.CreatedDateSort),
      UserName: row.UserName,
      Description: row.Description,
      ProductFamilyID: row.ProductFamilyID,
      SitesImpacted: row.SitesImpacted,
    }));

    normalizedRows.forEach(row => worksheet.addRow(row));

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1F305E' },
      };
      cell.alignment = { vertical: 'middle' };
    });

    const borderStyle = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };

    worksheet.eachRow(row => {
      row.eachCell(cell => {
        cell.border = borderStyle;
      });
    });

    exportColumns.forEach(column => {
      const worksheetColumn = worksheet.getColumn(column.key);

      if (column.key === 'Description') {
        worksheetColumn.width = 55;
        worksheetColumn.alignment = {
          vertical: 'top',
          wrapText: true,
        };
        return;
      }

      if (column.key === 'Title') {
        worksheetColumn.width = 40;
        worksheetColumn.alignment = {
          vertical: 'top',
          wrapText: true,
        };
        return;
      }

      let maxLength = column.label.length;
      normalizedRows.forEach(row => {
        const value = row[column.key];
        const valueLength = value ? String(value).length : 0;
        if (valueLength > maxLength) {
          maxLength = valueLength;
        }
      });

      worksheetColumn.width = Math.min(Math.max(maxLength + 2, 12), 45);
      worksheetColumn.alignment = {
        vertical: 'top',
        wrapText: false,
      };
    });

    const excelBuffer = await workbook.xlsx.writeBuffer();
    const excelBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const dateTag = new Date().toISOString().slice(0, 10);
    const typeTag = isRoleBasedMyPecr ? myPecrType : 'all';
    getDownloadLink(excelBlob, `my-pecr-list-${typeTag}-${dateTag}.xlsx`);
  };

  function filterCheckedItems(arr, key) {
    return arr
      .filter(item => item.checked)
      .map(item => item[key])
      .toString();
  }

  const getURLFilterData = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasFilterParams =
      searchParams.has('productFilter') ||
      searchParams.has('sitesFilter') ||
      searchParams.has('statusFilter') ||
      searchParams.has('roleFilter');

    if (!hasFilterParams) {
      return null;
    }

    return {
      selectedProductFamily: searchParams.get('productFilter') || '',
      selectedSites: searchParams.get('sitesFilter') || '',
      selectedStatus: searchParams.get('statusFilter') || '',
      selectedRole: searchParams.get('roleFilter') || '',
    };
  }, [location.search]);

  const updateURLWithFilters = useCallback(({
    selectedProductFamily = '',
    selectedSites = '',
    selectedStatus = '',
    selectedRole = '',
  }) => {
    const searchParams = new URLSearchParams(location.search);

    if (selectedProductFamily) {
      searchParams.set('productFilter', selectedProductFamily);
    } else {
      searchParams.delete('productFilter');
    }

    if (selectedSites) {
      searchParams.set('sitesFilter', selectedSites);
    } else {
      searchParams.delete('sitesFilter');
    }

    if (selectedStatus) {
      searchParams.set('statusFilter', selectedStatus);
    } else {
      searchParams.delete('statusFilter');
    }

    if (selectedRole) {
      searchParams.set('roleFilter', selectedRole);
    } else {
      searchParams.delete('roleFilter');
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isEmptyObject(userInfo) && userInfo.userID && userInfo.userID !== '') {
      //Get the List of my PECR
      fetch(`${apiConfig.uiURL}api/FetchLoginPECRList`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          userId: userInfo.userID.toString(),
        }),
      })
        .then(async response => {
          const data = await response.json();
          setListData(data);
          //Get the site list
          fetch(`${apiConfig.uiURL}api/SiteList`, {
            headers: APIHeader(),
          })
            .then(async response => {
              const data = await response.json();
              setSiteListData(data);
              //Get the status list
              fetch(`${apiConfig.uiURL}api/FetchListStatus`, {
                headers: APIHeader(),
              })
                .then(async response => {
                  const data = await response.json();
                  setStatusListData(data);
                  setIsLoading(false);
                })
                .catch(() => {
                  dispatch(
                    errorNotification({
                      hasErrorOccured: true,
                      message: t('error_message'),
                    })
                  );
                });
            })
            .catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            });
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiConfig, userInfo]);

  useEffect(() => {
    setFilterRoleData([
      { Role: 'creator', checked: true },
      { Role: 'owner', checked: true },
      { Role: 'actor', checked: true },
    ]);
  }, []);

  useEffect(() => {
    if (productFamilyList && productFamilyList.length > 0) {
      setFilterProdFamData(
        productFamilyList.map(item => ({
          ...item,
          checked: true,
        }))
      );
    }
  }, [productFamilyList]);

  useEffect(() => {
    if (siteListData && siteListData.length > 0) {
      setFilterSiteData(
        siteListData.map(item => ({
          ...item,
          checked: true,
        }))
      );
    }
  }, [siteListData]);

  useEffect(() => {
    if (statusListData && statusListData.length > 0) {
      setFilterStatusData(
        statusListData.map(item => ({
          ...item,
          checked: item.Status !== 'Complete' && item.Status !== 'Cancelled',
        }))
      );
    }
  }, [statusListData]);

  useEffect(() => {
    const urlFilterData = getURLFilterData();

    if (
      urlFilterRef.current ||
      !urlFilterData ||
      !userInfo?.userID ||
      productFamilyList.length === 0 ||
      siteListData.length === 0 ||
      statusListData.length === 0 ||
      (isAllMyPecr && filterRoleData.length === 0)
    ) {
      return;
    }

    urlFilterRef.current = true;

    const selectedProdFam = urlFilterData.selectedProductFamily
      ? urlFilterData.selectedProductFamily.split(',')
      : [];
    const selectedSites = urlFilterData.selectedSites
      ? urlFilterData.selectedSites.split(',')
      : [];
    const selectedStatus = urlFilterData.selectedStatus
      ? urlFilterData.selectedStatus.split(',')
      : [];
    const selectedRole = urlFilterData.selectedRole
      ? urlFilterData.selectedRole.split(',')
      : [];

    setFilterProdFamData(
      productFamilyList.map(item => ({
        ...item,
        checked: selectedProdFam.includes(item.Name),
      }))
    );
    setFilterSiteData(
      siteListData.map(item => ({
        ...item,
        checked: selectedSites.includes(item.SiteName),
      }))
    );
    setFilterStatusData(
      statusListData.map(item => ({
        ...item,
        checked: selectedStatus.includes(item.Status),
      }))
    );
    if (isAllMyPecr) {
      setFilterRoleData(prev =>
        prev.map(item => ({
          ...item,
          checked: selectedRole.includes(item.Role),
        }))
      );
    }

    setSelectedFilters(urlFilterData);
    setIsLoading(true);

    fetch(`${apiConfig.uiURL}api/FetchLoginPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        userId: userInfo.userID.toString(),
        filterStatus: urlFilterData.selectedStatus,
        filterSites: urlFilterData.selectedSites,
        filterProductFamily: urlFilterData.selectedProductFamily,
        sort:
          columnOrderData && columnOrderData.columnId
            ? columnOrderData.columnId
            : '',
        ascdesc:
          columnOrderData && columnOrderData.order ? columnOrderData.order : '',
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAllMyPecr,
    filterRoleData.length,
    location.search,
    userInfo,
    productFamilyList,
    siteListData,
    statusListData,
    apiConfig,
  ]);

  useEffect(() => {
    if (
      selectedFilters.selectedStatus ||
      selectedFilters.selectedSites ||
      selectedFilters.selectedProductFamily ||
      selectedFilters.selectedRole ||
      filterProdFamData.length === 0 ||
      filterSiteData.length === 0 ||
      filterStatusData.length === 0 ||
      (isAllMyPecr && filterRoleData.length === 0)
    ) {
      return;
    }

    const defaultFilters = {
      selectedStatus: filterCheckedItems(filterStatusData, 'Status'),
      selectedSites: filterCheckedItems(filterSiteData, 'SiteName'),
      selectedProductFamily: filterCheckedItems(filterProdFamData, 'Name'),
      selectedRole: isAllMyPecr
        ? filterCheckedItems(filterRoleData, 'Role')
        : '',
    };

    setSelectedFilters(defaultFilters);

    if (!getURLFilterData()) {
      updateURLWithFilters(defaultFilters);
    }
  }, [
    isAllMyPecr,
    filterProdFamData,
    filterSiteData,
    filterStatusData,
    filterRoleData,
    selectedFilters,
    getURLFilterData,
    updateURLWithFilters,
  ]);

  //navigating to tracking page based on pecrId
  const navigateToTrackingPage = pecrId => {
    navigate(`/TrackingFile?id=${pecrId}`);
  };

  //To handle the refresh button of Filter
  const onRefreshClick = filteredData => {
    setSelectedFilters(filteredData);
    updateURLWithFilters(filteredData);

    const selectedProdFam = filteredData.selectedProductFamily
      ? filteredData.selectedProductFamily.split(',')
      : [];
    const selectedSites = filteredData.selectedSites
      ? filteredData.selectedSites.split(',')
      : [];
    const selectedStatus = filteredData.selectedStatus
      ? filteredData.selectedStatus.split(',')
      : [];
    const selectedRole = filteredData.selectedRole
      ? filteredData.selectedRole.split(',')
      : [];

    setFilterProdFamData(prev =>
      prev.map(item => ({
        ...item,
        checked: selectedProdFam.includes(item.Name),
      }))
    );
    setFilterSiteData(prev =>
      prev.map(item => ({
        ...item,
        checked: selectedSites.includes(item.SiteName),
      }))
    );
    setFilterStatusData(prev =>
      prev.map(item => ({
        ...item,
        checked: selectedStatus.includes(item.Status),
      }))
    );
    if (isAllMyPecr) {
      setFilterRoleData(prev =>
        prev.map(item => ({
          ...item,
          checked: selectedRole.includes(item.Role),
        }))
      );
    }

    // Get the List of PECR based on selected filter
    fetch(`${apiConfig.uiURL}api/FetchLoginPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        userId: userInfo.userID.toString(),
        filterStatus: filteredData.selectedStatus,
        filterSites: filteredData.selectedSites,
        filterProductFamily: filteredData.selectedProductFamily,
        sort:
          columnOrderData && columnOrderData.columnId
            ? columnOrderData.columnId
            : '',
        ascdesc:
          columnOrderData && columnOrderData.order ? columnOrderData.order : '',
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  //To handle the sorting of columns in List table
  const handleOrderChange = (columnId, order) => {
    setColumnOrderData({
      columnId: columnId,
      order: order,
    });
    // Get the List of PECR based on sorted column and order
    fetch(`${apiConfig.uiURL}api/FetchLoginPECRList`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        userId: userInfo.userID.toString(),
        sort: columnId,
        ascdesc: order,
        filterStatus: selectedFilters.selectedStatus,
        filterSites: selectedFilters.selectedSites,
        filterProductFamily: selectedFilters.selectedProductFamily,
      }),
    })
      .then(async response => {
        const data = await response.json();
        setListData(data);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  if (errorInAPI.hasErrorOccured) {
    return <ErrorAlert message={errorInAPI.message} />;
  }

  return (
    <div className="flex flex-col">
      <PageHeader title={pageTitle}>
        <Box sx={{ position: 'relative' }}>
          <Button
            variant="contained"
            onClick={handleDownloadExcel}
            sx={{
              textTransform: 'none',
              fontSize: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
              },
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
            disabled={isLoading || isRoleListLoading || displayListData.length === 0}
          >
            {t('Download_Excel')}
          </Button>
        </Box>
      </PageHeader>
      <Box style={modernContentSection}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Grid style={ListFilterTableGrid}>
            <Grid>
              {siteListData.length > 0 ? (
                <Filter
                  productFamilyList={filterProdFamData}
                  siteListData={filterSiteData}
                  statusListData={filterStatusData}
                  roleListData={isAllMyPecr ? filterRoleData : []}
                  handleRefreshClick={onRefreshClick}
                  filterMaxHeight={592}
                />
              ) : (
                <Grid style={homeButtonComponent} width={300}>
                  <CircularProgress />
                </Grid>
              )}
            </Grid>
            {isRoleListLoading ? (
              <CircularProgress />
            ) : displayListData.length > 0 ? (
              <Grid>
                <ListTable
                  isMyPecrList={true}
                  rows={displayListData}
                  navigateToTrackingPage={navigateToTrackingPage}
                  orderHandler={handleOrderChange}
                />
              </Grid>
            ) : (
              <Grid style={homeButtonComponent}>
                <Typography style={{ padding: '10px', fontWeight: 'bold' }}>
                  {t('No_result_found')}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </div>
  );
}

const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
    productFamilyList: state.pecrInfo.pecrData.productFamilyList,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(MyPecr);
